import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "~/common/components/Table/TableElements";
import { DateFormatType, formatDate } from "~/common/utils/DateFormatter";
import { SortDirection, useSorting } from "~/common/hooks/useSorting";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ContractLine } from "~/features/contracts/models/models";
import { Link, LinkType } from "~/common/components/Link";
import { resolveUrl } from "~/utils/url/urlResolver";
import Spinner from "~/common/components/Spinner/Spinner";
import { styled, TableContainer } from "~/common/mui";

const StyledCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== "pastDelivery",
})<{ pastDelivery?: boolean }>(({ theme, pastDelivery }) => ({
  verticalAlign: "middle !important",
  "& > p": { margin: 0 },
  ...(pastDelivery && {
    color: `${theme.palette.action.disabled} !important`,
  }),
}));

const HeaderLabelCell = styled(TableCell)(({ theme }) => ({
  fontWeight: "bold !important",
  backgroundColor: `${theme.palette.common.white} !important`,
  width: "30%",
}));

interface NewContractTableProps {
  contractLines?: ContractLine[];
}

export enum ContractSort {
  disposition = "disposition",
  orderQuantity = "orderQuantity",
  openQuantity = "openQuantity",
  orderNumber = "orderNumber",
}

const SORT_HANDLERS: Dict<(a: ContractLine, b: ContractLine) => number> = {
  [ContractSort.disposition]: (a, b) => {
    const aTime = a.disposition || 0;
    const bTime = b.disposition || 0;
    return aTime === bTime ? 0 : aTime > bTime ? 1 : -1;
  },
  [ContractSort.orderNumber]: (a, b) => parseInt(a.orderNumber) - parseInt(b.orderNumber),
  [ContractSort.openQuantity]: (a, b) => {
    if (a.openQuantity === 0 && b.openQuantity === 0) {
      return (new Date(a.disposition).getTime() || 0) - (new Date(b.disposition).getTime() || 0);
    } else {
      return a.openQuantity === 0 ? -1 : b.openQuantity === 0 ? 1 : 0;
    }
  },
};

const NewDeliveriesTable = ({ contractLines }: NewContractTableProps) => {
  const { t } = useTranslation();
  const [sortCode, setSortCode] = useState(ContractSort.openQuantity);
  const [sortDirection, setSortDirection] = useState(SortDirection.Desc);

  const rows = useSorting(contractLines ?? [], SORT_HANDLERS, sortCode, sortDirection);

  if (!contractLines) {
    return <Spinner />;
  }

  const createSortHandler = (codeToSet: ContractSort) => () => {
    if (codeToSet === sortCode) {
      setSortDirection(sortDirection === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc);
    } else {
      setSortCode(codeToSet);
      setSortDirection(SortDirection.Asc);
    }
  };

  const createTableProps = (boundSortCode: ContractSort) => ({
    onClick: createSortHandler(boundSortCode),
    active: boundSortCode === sortCode,
    direction: boundSortCode === sortCode ? sortDirection : SortDirection.Desc,
  });

  const contractEntries = rows?.map((contract) => {
    const disposition = formatDate(contract.disposition, DateFormatType.DATE);
    const orderDetailUrl = resolveUrl(`/my-account/order?id=${contract.orderNumber}`);
    const noQuantity = contract.openQuantity === 0;
    return (
      <TableRow key={`${contract.orderNumber}-${contract.salesOrderLine ?? ""}-${contract.scheduleLineNum}`}>
        <StyledCell pastDelivery={noQuantity}>
          <p>{disposition ?? "-"}</p>
        </StyledCell>
        <StyledCell pastDelivery={noQuantity}>
          <p>{contract.orderQuantity ?? "-"}</p>
        </StyledCell>
        <StyledCell pastDelivery={noQuantity}>
          <p>{contract.openQuantity ?? "-"}</p>
        </StyledCell>
        <StyledCell align="right">
          <Link variant={LinkType.NAVIGATION} className="f-bold" href={orderDetailUrl}>
            <p>{contract.orderNumber}</p>
          </Link>
        </StyledCell>
      </TableRow>
    );
  });
  return (
    <TableContainer sx={{ maxHeight: 500 }}>
      <Table stickyHeader sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            <HeaderLabelCell>
              <TableSortLabel {...createTableProps(ContractSort.disposition)}>
                {t("contractDetails.table.disposition")}
              </TableSortLabel>
            </HeaderLabelCell>
            <HeaderLabelCell>{t("contractDetails.table.orderQuantity")}</HeaderLabelCell>
            <HeaderLabelCell>{t("contractDetails.openQuantity")}</HeaderLabelCell>
            <HeaderLabelCell align="right" sx={{ width: "auto", wordBreak: "break-word" }}>
              <TableSortLabel {...createTableProps(ContractSort.orderNumber)}>
                {t("contractDetails.table.orderNumber")}
              </TableSortLabel>
            </HeaderLabelCell>
          </TableRow>
        </TableHead>
        <TableBody>{contractEntries}</TableBody>
      </Table>
    </TableContainer>
  );
};

export default NewDeliveriesTable;
